/**
 * Post-auth age gate: when to block /app/* behind a birthdate prompt.
 * AGE_BACKFILL_PROMPT default off — existing accounts are not prompted.
 * New Google / Arena / magic-link sessions (first minutes of the auth user)
 * always hit the same gate so those paths cannot skip the check.
 */

import { getFeatureFlag } from "@/config/featureFlagsCentral";
import type { User } from "@supabase/supabase-js";

export const AGE_GATE_PATH = "/auth/age";
export const SIGNUP_DOB_STORAGE_KEY = "gg_signup_dob";

/** Roles that must never see the prompt or be tombstoned. */
export const AGE_GATE_STAFF_ROLES = [
  "app_admin",
  "org_admin",
  "marketing_admin",
  "coach",
  "educator",
  "educator_provider",
  "viewer",
] as const;

export type AgeGateStaffRole = (typeof AGE_GATE_STAFF_ROLES)[number];

/** Accounts created this recently are treated as incomplete OAuth/magic-link signups. */
export const NEW_ACCOUNT_WINDOW_MS = 15 * 60 * 1000;

export function isAgeBackfillPromptEnabled(): boolean {
  return getFeatureFlag("AGE_BACKFILL_PROMPT");
}

export function isAgeGateStaffRole(role: string | null | undefined): boolean {
  if (!role) return false;
  return (AGE_GATE_STAFF_ROLES as readonly string[]).includes(role);
}

export function isNewAuthAccount(user: Pick<User, "created_at"> | null | undefined): boolean {
  if (!user?.created_at) return false;
  const created = new Date(user.created_at).getTime();
  if (Number.isNaN(created)) return false;
  return Date.now() - created < NEW_ACCOUNT_WINDOW_MS;
}

export function shouldPromptAgeGate(opts: {
  hasVerificationRow: boolean;
  isStaff: boolean;
  isNewAccount: boolean;
  backfillEnabled: boolean;
}): boolean {
  if (opts.hasVerificationRow || opts.isStaff) return false;
  if (opts.backfillEnabled) return true;
  return opts.isNewAccount;
}

export function ageGateUrl(redirect?: string | null): string {
  const params = new URLSearchParams();
  if (redirect && redirect.startsWith("/") && !redirect.startsWith("//")) {
    params.set("redirect", redirect);
  }
  const q = params.toString();
  return q ? `${AGE_GATE_PATH}?${q}` : AGE_GATE_PATH;
}

export function readStoredSignupDob(): string | null {
  try {
    return sessionStorage.getItem(SIGNUP_DOB_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function writeStoredSignupDob(dob: string): void {
  try {
    sessionStorage.setItem(SIGNUP_DOB_STORAGE_KEY, dob);
  } catch {
    /* private mode */
  }
}

export function clearStoredSignupDob(): void {
  try {
    sessionStorage.removeItem(SIGNUP_DOB_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
